import React, {ReactNode, useEffect, useState} from "react";
import {
  BackendFetch,
  LanguageStorage,
  Tolgee,
  TolgeeInstance,
  TolgeeProvider,
  DevBackend,
} from "@tolgee/react";
import {FormatIcu} from "@tolgee/format-icu";
import {CircularProgress} from "@material-ui/core";
import {
  ENGLISH_LANG_CODE,
  LocalizationLanguageStatus,
} from "@evennt-tech/api-v2-core";
import {localizationLanguageAdminStore} from "store/localization/localization-language-admin.store";

const CDN_URL =
  "https://public-file.ffair.show/locates/c828882f1c9a413cb8c26b043a2b97b9";

const apiKey = import.meta.env.VITE_APP_TOLGEE_API_KEY;

const createTolgee = (availableLanguages: string[]): TolgeeInstance => {
  let instance = Tolgee().use(FormatIcu()).use(LanguageStorage());
  if (!apiKey) {
    instance = instance.use(BackendFetch({prefix: CDN_URL, cache: "no-cache"}));
  } else {
    instance = instance.use(DevBackend());
  }

  return instance.init({
    defaultLanguage: ENGLISH_LANG_CODE,
    fallbackLanguage: ENGLISH_LANG_CODE,
    availableLanguages,
    projectId: import.meta.env.VITE_TOLGEE_PROJECT_ID,
    ...(import.meta.env.VITE_APP_TOLGEE_API_KEY && {
      apiUrl: import.meta.env.VITE_TOLGEE_BASE_URL,
      apiKey: import.meta.env.VITE_APP_TOLGEE_API_KEY,
    }),
  });
};

const AppTolgeeProvider = ({children}: {children: ReactNode}) => {
  const [tolgee, setTolgee] = useState<TolgeeInstance | null>(null);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      await localizationLanguageAdminStore.getState().fetchLanguages();

      const {languages} = localizationLanguageAdminStore.getState();
      const publishedCodes = languages
        .filter(
          (language) => language.status === LocalizationLanguageStatus.Published
        )
        .map((language) => language.code);

      if (cancelled) return;

      setTolgee(
        createTolgee(
          Array.from(new Set([ENGLISH_LANG_CODE].concat(publishedCodes)))
        )
      );
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!tolgee) {
    return <CircularProgress size={24} />;
  }

  return (
    <TolgeeProvider fallback={<CircularProgress size={24} />} tolgee={tolgee}>
      {children}
    </TolgeeProvider>
  );
};

export default AppTolgeeProvider;
